/**
 * The unauthenticated front door — the surface shown until `4f:token` is written.
 * Pure composition over `@4f/ui` + the injected {@link SessionStore} (DIP): it
 * owns only the login⇄sign-up mode toggle and delegates each mode to its own
 * form ({@link LoginForm} / {@link SignupWizard}). Full-screen (it replaces the
 * whole two-pane surface), themed + i18n'd through the shared providers, so it
 * inherits light/dark + locale with no extra wiring.
 */
import { Button, useT } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { SessionStore } from '../../stores/session.ts';
import type { AuthMode } from './auth-model.ts';
import { LoginForm } from './LoginForm.tsx';
import { SignupWizard } from './SignupWizard.tsx';

export interface AuthSurfaceProps {
  readonly session: SessionStore;
  /** An invite code from a referral link (`?ref=`), forwarded to sign-up. */
  readonly referralCode?: string;
  /** Which mode to open on. Defaults to `login`. */
  readonly initialMode?: AuthMode;
}

export function AuthSurface(props: AuthSurfaceProps): JSX.Element {
  const { t } = useT();
  // An invited visitor arrived from someone's link — open on SIGN-UP, not login.
  // Landing a referral on the login form is the single easiest way to lose the
  // conversion the invite just earned.
  const [mode, setMode] = createSignal<AuthMode>(
    props.initialMode ?? (props.referralCode ? 'signup' : 'login'),
  );
  const isSignup = (): boolean => mode() === 'signup';

  return (
    <main class="f4-auth">
      <section class="f4-auth__card" aria-labelledby="auth-title">
        <h1 id="auth-title" class="f4-auth__title">
          {t('auth.title')}
        </h1>
        <p class="f4-auth__subtitle">
          {isSignup() ? t('auth.signup.prompt') : t('auth.login.prompt')}
        </p>

        <Show when={isSignup()} fallback={<LoginForm session={props.session} />}>
          <SignupWizard session={props.session} referralCode={props.referralCode} />
        </Show>

        <p class="f4-auth__switch">
          {isSignup() ? t('auth.haveAccount') : t('auth.noAccount')}{' '}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setMode(isSignup() ? 'login' : 'signup')}
          >
            {isSignup() ? t('auth.login.submit') : t('auth.signup.submit')}
          </Button>
        </p>
      </section>
    </main>
  );
}
